import React, { useCallback, useRef } from 'react';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';
import {
  ClearRefinements,
  Configure,
  ConfigureProps,
  Hits,
  InstantSearch,
  Pagination,
  SearchBox,
  useCurrentRefinements,
  useSearchBox,
} from 'react-instantsearch';
import { Panel } from '@src/components/UI/QuestionBank/SearchFilters';
import { CustomSelect } from '@src/components/Search/Navigate/Select';
import * as styles from './search.module.css';
import { graphql } from 'gatsby';
import { hasRefinement } from '@src/utils/search';
import { showExamBoard, showModule, showTier } from '@src/settings/search';
import ReactMarkdown from 'react-markdown';
import { useShowMoreInformation, useSortByModules, useSortByPracticeType } from '@src/hooks/search';
import HitMe from '@src/components/Search/Hit/Hit';
import { sendEvent } from '@src/utils/gtag';

const typesenseInstantsearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.GATSBY_TYPESENSE_SEARCH_ONLY_API_KEY, // Be sure to use the search-only-api-key
    nodes: [
      {
        host: process.env.GATSBY_TYPESENSE_HOST,
        port: 443,
        protocol: 'https',
      },
    ],
  },
  collectionSpecificSearchParameters: {
    documents: {
      sort_by: 'priority:asc,name:asc',
      query_by:
        'name,doc_exam_course,doc_exam_board,doc_type,doc_past_paper_type,doc_exam_practice_type,doc_exam_tier,doc_exam_course_code,doc_exam_modules,doc_related_topics',
    },
  },
});

const searchClient = typesenseInstantsearchAdapter.searchClient;

const future = { preserveSharedStateOnUnmount: true };

const SearchFilters = ({ examCourses }: { examCourses: any[] }) => {
  const currentFilters = useCurrentRefinements();
  const { query } = useSearchBox();
  const showMoreInformation = useShowMoreInformation();

  const sortByPracticeType = useSortByPracticeType();
  const sortByModules = useSortByModules();

  const sortByName = useCallback((a: any, b: any) => {
    const examCourseA = examCourses.find((course) => course.name === a.name);
    const examCourseB = examCourses.find((course) => course.name === b.name);

    if (examCourseA && examCourseB) {
      return examCourseA.position - examCourseB.position;
    }

    return a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : 1;
  }, []);

  const selected = {
    courseCode: hasRefinement(currentFilters.items, 'doc_exam_course', ['Cambridge']),
    pastPapers: hasRefinement(currentFilters.items, 'doc_type', ['Past Papers']),
    practicePapers: hasRefinement(currentFilters.items, 'doc_type', ['Practice Papers']),
    examBoard: hasRefinement(currentFilters.items, 'doc_exam_course', showExamBoard),
    tier: hasRefinement(currentFilters.items, 'doc_exam_course', showTier),
    module: hasRefinement(currentFilters.items, 'doc_exam_course', showModule),
  };

  const { clear: clearSearch } = useSearchBox();

  const seen = useRef<Set<string>>(new Set());

  const handleInteraction = (id: string) => {
    console.log('handleInteraction', id);

    if (seen.current.has(id)) {
      return;
    }

    seen.current.add(id);

    sendEvent({
      name: 'search_used',
      category: 'search',
      label: id,
      value: seen.current.size,
    });
  };

  return (
    <>
      <SearchBox
        placeholder=" Type here to search via text on any document or to quick search any of the filters above"
        className={styles.searchbox}
        style={{
          marginBottom: 20,
        }}
      />

      {query.length > 0 && (
        <p
          style={{
            textAlign: 'left',
            fontSize: '14px',
            marginBottom: 20,
          }}
        >
          Also filtering via search query <b>{query}</b> (click "x" on search box to remove)
        </p>
      )}
      <div className={styles.searchContainer}>
        <div className={styles.searchFilters}>
          <Panel header="Exam Course">
            <CustomSelect
              attribute="doc_exam_course"
              onChange={() => handleInteraction('exam_course')}
              sortBy={sortByName}
              formatLabel={(label) =>
                `${label.includes('(GCE)') ? label.replace('(GCE)', '') : label}`
              }
            />
          </Panel>

          {selected.examBoard && (
            <Panel header="Exam Board">
              <CustomSelect attribute="doc_exam_board" sortBy={['name:asc']} />
            </Panel>
          )}

          {showMoreInformation && (
            <Panel header="Resource Type">
              <CustomSelect
                attribute="doc_type"
                sortBy={['name:asc']}
                includeCount={false}
                onChange={() => handleInteraction('resource_type')}
              />
            </Panel>
          )}

          <ClearRefinements
            className="button w-button search-button"
            translations={{
              resetButtonText: 'Clear Filters',
            }}
            onPointerDown={() => {
              clearSearch();
            }}
          />
        </div>

        {showMoreInformation && (selected.pastPapers || selected.practicePapers) && (
          <div className={styles.searchFilters}>
            <Panel header="Past Paper Type">
              <CustomSelect attribute="doc_past_paper_type" sortBy={['name:asc']} />
            </Panel>
            <Panel header="Practice Type">
              <CustomSelect attribute="doc_exam_practice_type" sortBy={sortByPracticeType} />
            </Panel>
            <Panel header="Year">
              <CustomSelect attribute="doc_exam_year" />
            </Panel>
          </div>
        )}

        {showMoreInformation && (
          <div className={styles.searchFilters}>
            {selected.module && (
              <Panel header="Module">
                <CustomSelect attribute="doc_exam_modules" sortBy={sortByModules} />
              </Panel>
            )}
            {selected.courseCode && (
              <Panel header="Course Code">
                <CustomSelect attribute="doc_exam_course_code" />
              </Panel>
            )}
            <Panel header="Topic">
              <CustomSelect attribute="doc_related_topics" sortBy={['name:asc']} />
            </Panel>
            {selected.tier && (
              <Panel header="Tier">
                <CustomSelect attribute="doc_exam_tier" />
              </Panel>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const Results = () => {
  const showMoreInformation = useShowMoreInformation();

  if (!showMoreInformation) {
    return null;
  }

  return (
    <>
      <div className={`${styles.spacer} container`}>
        <Hits
          classNames={{
            item: styles.hitItem,
            list: styles.hitList,
          }}
          hitComponent={HitMe}
        />
      </div>
      <Pagination
        style={{
          margin: '0 auto',
          width: 'fit-content',
        }}
      />
    </>
  );
};

const Page = ({
  data: {
    allDatoCmsExamCourse,
    datoCmsPage: { description },
  },
}) => {
  const configureProps = {
    hitsPerPage: 12,
  } as ConfigureProps;

  return (
    <InstantSearch searchClient={searchClient} indexName="documents" future={future} routing>
      <Configure {...configureProps} />
      <br />
      <div className={`${styles.spacer} container`}>
        <h2>Resource Search</h2>
        {description && <ReactMarkdown className={styles.description} children={description} />}

        <SearchFilters examCourses={allDatoCmsExamCourse.nodes} />
      </div>
      <Results />
    </InstantSearch>
  );
};

export default Page;

const query = graphql`
  query {
    allDatoCmsExamCourse(sort: { fields: position }, filter: { searchFilter: { eq: true } }) {
      nodes {
        name
        position
      }
    }
    datoCmsPage(slug: { eq: "search" }) {
      description
    }
  }
`;

export { query };
